import type { SidebarItem } from './sidebar';
import React, { FC } from 'react';
import { Links, useLocation, useNavigate } from 'react-router';
import {
  User,
  Badge,
  Avatar,
  Button,
  ScrollShadow,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  Spacer,
} from '@heroui/react';
import { Icon } from '@iconify/react';

import { AcmeIcon, GridIcon, AccountIcon, Cart, Bill } from './acme';

import NotificationsCard from './notifications-card';
import Sidebar from './sidebar';
import SidebarChip from './sidebar-chip';
import { useAuth } from '../../contexts/AuthContext';
import { useAccount } from '../../contexts/AccountContext';
import { useTranslations } from '../../contexts/translation.context';
import Typography from '../typography/Typography';
import { Link } from 'react-router';
import { LogoCreatorIc } from '../icons';
import IcTg from '../../client/ic-telegram.svg';

// Мемоизированные иконки для предотвращения ререндеров
const DashboardIcon = React.memo(() => {
  const { t } = useTranslations();
  return (
    <GridIcon
      className="text-default-400 group-data-[selected=true]:text-white"
      aria-label={t('aria.dashboardIcon')}
    />
  );
});
DashboardIcon.displayName = 'DashboardIcon';

const AccountsIcon = React.memo(() => {
  const { t } = useTranslations();
  return (
    <AccountIcon
      className="text-default-400 group-data-[selected=true]:text-white"
      aria-label={t('aria.accountsIcon')}
    />
  );
});
AccountsIcon.displayName = 'AccountsIcon';

const AccountsChip = React.memo(() => {
  const { pagination } = useAccount();
  const accountCount = pagination?.count || 0;

  return (
    <SidebarChip ariaLabel={`Количество аккаунтов: ${accountCount}`}>{accountCount}</SidebarChip>
  );
});
AccountsChip.displayName = 'AccountsChip';

// Мемоизированные компоненты для Offers секции
const BillIcon = React.memo(() => {
  const { t } = useTranslations();
  return (
    <Bill
      className="text-default-400 group-data-[selected=true]:text-white"
      aria-label={t('aria.billsIcon')}
    />
  );
});
BillIcon.displayName = 'BillIcon';

const OffersChip = React.memo(() => {
  const { revshareOffersLength } = useAuth();
  return (
    <SidebarChip ariaLabel={`Количество офферов: ${revshareOffersLength}`}>
      {revshareOffersLength}
    </SidebarChip>
  );
});
OffersChip.displayName = 'OffersChip';

const CampaignsChip = React.memo(() => {
  const { revshareCampaignsLength } = useAuth();
  const { t } = useTranslations();
  const total = Number(revshareCampaignsLength) || 0;
  return <SidebarChip ariaLabel={`${t('aria.campaignsCount')}: ${total}`}>{total}</SidebarChip>;
});
CampaignsChip.displayName = 'CampaignsChip';

const BillsChip = React.memo(() => {
  const { t } = useTranslations();
  const { revshareBillsLength } = useAuth();

  return (
    <SidebarChip ariaLabel={`${t('aria.billsCount')}: ${revshareBillsLength || 0}`}>
      {revshareBillsLength || 0}
    </SidebarChip>
  );
});
BillsChip.displayName = 'BillsChip';

// Мемоизированные компоненты для подписчиков
const ProposalsChip = React.memo(() => {
  const { proposalsLength } = useAuth();
  return (
    <SidebarChip ariaLabel={`Количество предложений: ${proposalsLength}`}>
      {proposalsLength}
    </SidebarChip>
  );
});
ProposalsChip.displayName = 'ProposalsChip';

const OrdersIcon = React.memo(() => {
  const { t } = useTranslations();
  return (
    <Cart
      className="text-default-400 group-data-[selected=true]:text-white"
      aria-label={t('aria.ordersIcon')}
    />
  );
});
OrdersIcon.displayName = 'OrdersIcon';

const SubscribersGroupIcon = React.memo(() => {
  const { t } = useTranslations();
  return (
    <Icon
      icon="solar:users-group-two-rounded-outline"
      className="text-default-400"
      aria-label={t('aria.subscribersIcon')}
    />
  );
});
SubscribersGroupIcon.displayName = 'SubscribersGroupIcon';

const StatsGroupIcon = React.memo(() => {
  const { t } = useTranslations();
  return (
    <Icon
      icon="solar:pie-chart-2-outline"
      className="text-default-400"
      aria-label={t('aria.statisticsIcon')}
    />
  );
});
StatsGroupIcon.displayName = 'StatsGroupIcon';

const AccountGroupIcon = React.memo(() => {
  const { t } = useTranslations();
  return (
    <Icon
      icon="solar:settings-outline"
      className="text-default-400"
      aria-label={t('aria.accountIcon')}
    />
  );
});
AccountGroupIcon.displayName = 'AccountGroupIcon';

const OrdersChip = React.memo(() => {
  const { ordersLength, ordersActiveLength } = useAuth();
  const { t } = useTranslations();
  return (
    <SidebarChip ariaLabel={`${t('aria.ordersCount')}: ${ordersActiveLength} из ${ordersLength}`}>
      {ordersActiveLength} / {ordersLength}
    </SidebarChip>
  );
});
OrdersChip.displayName = 'OrdersChip';
const WalletChip = React.memo(() => {
  const { user } = useAuth();
  return (
    <SidebarChip ariaLabel={`Баланс: $${user?.balance?.toFixed(2) || '0.00'}`}>
      ${user?.balance?.toFixed(2) || '0.00'}
    </SidebarChip>
  );
});
// Функция для создания сайдбар элементов с переводами
const createSidebarItems = (t: (key: string) => string): SidebarItem[] => [
  {
    key: 'home',
    href: '/client/dashboard',
    startContent: <DashboardIcon />,
    title: t('sidebar.dashboard'),
  },
  {
    key: 'accounts',
    href: '/client/accounts',
    startContent: <AccountsIcon />,
    title: t('sidebar.accounts'),
    endContent: <AccountsChip />,
  },
  {
    key: 'proposals',
    title: t('sidebar.subscribers'),
    startContent: <SubscribersGroupIcon />,
    items: [
      {
        key: 'proposals',
        href: '/client/subscribers/proposals',
        icon: 'solar:checklist-minimalistic-outline',
        title: t('sidebar.proposals'),
        endContent: <ProposalsChip />,
      },
      {
        key: 'orders',
        href: '/client/subscribers/orders',
        startContent: <OrdersIcon />,
        title: t('sidebar.orders'),
        endContent: <OrdersChip />,
      },
    ],
  },
  {
    key: 'revshare',
    title: t('sidebar.offersRevshare'),
    startContent: <BillIcon />,
    items: [
      {
        key: 'offers-list',
        href: '/client/revshare/offers',
        icon: 'solar:bill-list-outline',
        title: t('sidebar.offers'),
        endContent: <OffersChip />,
      },
      {
        key: 'campaigns',
        href: '/client/revshare/campaigns',
        icon: 'solar:star-line-duotone',
        title: t('sidebar.campaigns'),
        endContent: <CampaignsChip />,
      },
      {
        key: 'bills',
        href: '/client/revshare/bills',
        startContent: <BillIcon />,
        title: t('sidebar.bills'),
        endContent: <BillsChip />,
      },
    ],
  },
  {
    key: 'stats',
    title: t('sidebar.statistics'),
    startContent: <StatsGroupIcon />,
    items: [
      {
        key: 'statistics',
        href: '/client/statistics',
        icon: 'solar:pie-chart-2-outline',
        title: t('sidebar.statistics'),
      },
      {
        key: 'subscribers',
        href: '/client/statistics/subscribers',
        icon: 'solar:users-group-two-rounded-outline',
        title: t('sidebar.subscribers'),
      },
      {
        key: 'transactions',
        href: '/client/statistics/transactions',
        icon: 'solar:card-transfer-outline',
        title: t('sidebar.transactions'),
      },
    ],
  },
  {
    key: 'account',
    title: t('sidebar.account'),
    startContent: <AccountGroupIcon />,
    items: [
      {
        key: 'wallet',
        href: '/client/account/wallet',
        icon: 'solar:wallet-linear',
        title: t('sidebar.wallet'),
        endContent: <WalletChip />,
      },
      {
        key: 'settings',
        href: '/client/account/settings',
        icon: 'solar:settings-outline',
        title: t('sidebar.settings'),
      },
    ],
  },
];

const SidebarActiveTracker = React.memo<{ onItemClick?: () => void }>(({ onItemClick }) => {
  const location = useLocation();
  const deferredPathname = React.useDeferredValue(location.pathname);
  const { t } = useTranslations();

  const sidebarItems = React.useMemo(() => createSidebarItems(t), [t]);

  const selectedKey = React.useMemo(() => {
    let bestMatch = { key: 'home', matchLength: 0 };

    for (const item of sidebarItems) {
      if (item.href) {
        if (deferredPathname === item.href) {
          return item.key;
        } else if (deferredPathname.startsWith(item.href + '/')) {
          if (item.href.length > bestMatch.matchLength) {
            bestMatch = { key: item.key, matchLength: item.href.length };
          }
        }
      }
      if (item.items) {
        for (const subItem of item.items) {
          if (subItem.href) {
            if (deferredPathname === subItem.href) {
              return subItem.key;
            } else if (deferredPathname.startsWith(subItem.href + '/')) {
              if (subItem.href.length > bestMatch.matchLength) {
                bestMatch = { key: subItem.key, matchLength: subItem.href.length };
              }
            }
          }
        }
      }
    }

    return bestMatch.key;
  }, [deferredPathname, sidebarItems]);

  return (
    <SidebarContent
      selectedKey={selectedKey}
      sidebarItems={sidebarItems}
      onItemClick={onItemClick}
    />
  );
});

const SidebarContent = React.memo<{
  selectedKey: string;
  sidebarItems: SidebarItem[];
  onItemClick?: () => void;
}>(({ selectedKey, sidebarItems, onItemClick }) => {
  const [isPending, startTransition] = React.useTransition();
  const { user } = useAuth();

  const sidebarProps = React.useMemo(
    () => ({
      iconClassName: '',
      items: sidebarItems,
      selectedKey,
      isPending,
      startTransition,
      userRole: user?.role,
    }),
    [selectedKey, isPending, user?.role, sidebarItems]
  );

  return <Sidebar {...sidebarProps} onItemClick={onItemClick} />;
});

SidebarContent.displayName = 'SidebarContent';
const getInitials = (name: any) => {
  if (!name) return '';

  const words = name.trim().split(' ');

  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  } else if (words.length >= 2) {
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  }

  return '';
};

// Изолированный компонент хедера
const SidebarHeader = React.memo<{ onClose?: () => void }>(({ onClose }) => {
  const { logout } = useAuth();
  const { pagination, accounts } = useAccount();
  const navigate = useNavigate();
  const accountCount = pagination?.count || 0;
  const { user, notifications } = useAuth();
  const { t } = useTranslations();
  const handleNavigate = React.useCallback(
    (path: string) => {
      onClose?.();
      navigate(path);
    },
    [navigate, onClose]
  );
  const handleLogout = React.useCallback(() => {
    onClose?.();
    logout();
  }, [logout, onClose]);
  return (
    <div className="flex items-center justify-between md:pt-0 pt-7">
      <div className="flex items-center gap-2 md:px-2">
        <Link to="/client/dashboard" className="icon h-[1.375rem] w-[4.75rem]">
          <LogoCreatorIc />
        </Link>
        <span className="text-xs font-regular text-auth-muted select-none">
          {user?.type === 'creator'
            ? t('sidebar.creator')
            : user?.type === 'agency'
              ? t('sidebar.agency')
              : t('sidebar.marketer')}
        </span>
      </div>
      <div className="flex items-center justify-end">
        <Popover offset={12} placement="bottom-start" aria-label={t('aria.notificationsPanel')}>
          <PopoverTrigger>
            <Button
              disableRipple
              isIconOnly
              className="overflow-visible"
              radius="full"
              variant="light"
              aria-label={t('aria.notifications')}
            >
              {/* @ts-ignore */}
              {notifications?.objects?.filter((n: Notification) => !n.was_read).length > 0 ? (
                //@ts-ignore
                <Badge
                  color="danger"
                  className="bg-dangerDeep w-4 top-[0.1875rem] right-[0.3125rem] h-4"
                  content={notifications?.objects?.filter((n: any) => !n.was_read).length}
                  showOutline={false}
                  size="sm"
                  aria-label="Уведомления"
                  classNames={{
                    base: 'text-white text-xxs leading-4 font-regular',
                  }}
                >
                  <Icon
                    className="text-default-500"
                    icon="solar:bell-outline"
                    width={24}
                    height={24}
                    aria-label={t('aria.notifications')}
                  />
                </Badge>
              ) : (
                <Icon
                  className="text-default-500"
                  icon="solar:bell-outline"
                  width={24}
                  height={24}
                  aria-label={t('aria.notifications')}
                />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="max-w-[90vw] p-0 md:max-w-[27.4375rem]">
            <NotificationsCard className="w-full shadow-none" />
          </PopoverContent>
        </Popover>
        {/* User Menu */}
        <Dropdown showArrow placement="bottom-start" aria-label={t('aria.userMenu')}>
          <DropdownTrigger>
            <Button
              disableRipple
              isIconOnly
              className="-mr-1"
              radius="full"
              variant="light"
              aria-label={t('aria.userMenu')}
            >
              <div className="border border-default-300 rounded-full p-0.5">
                <Avatar
                  className="h-7 w-7 cursor-pointer"
                  name={getInitials(user?.name)}
                  src={user?.avatar}
                  aria-label={`${t('aria.userProfile')} ${user?.name}`}
                />
              </div>
            </Button>
          </DropdownTrigger>
          <DropdownMenu aria-label="Custom item styles" disabledKeys={['profile']}>
            <DropdownSection aria-label="Profile & Actions">
              <DropdownItem
                key="profile"
                isReadOnly
                className="h-14 gap-2 opacity-100"
                textValue="Signed in as"
              >
                <User
                  avatarProps={{
                    size: 'sm',
                    imgProps: {
                      className: 'transition-none',
                    },
                    src: user?.avatar,
                    name: getInitials(user?.name),
                  }}
                  classNames={{
                    name: 'text-default-600',
                    description: 'text-default-500',
                  }}
                  // description={user?.tg_tag}
                  name={user?.name}
                  aria-label={`Информация о пользователе ${user?.name}`}
                />
              </DropdownItem>
              <>
                {accounts?.length > 0 &&
                  accounts?.slice(0, 5).map((item: any) => (
                    <DropdownItem
                      key={item?.of_user_id}
                      className="opacity-100 py-0"
                      textValue="Signed in as"
                    >
                      <AccountItem item={item} onClick={onClose} />
                    </DropdownItem>
                  ))}
              </>
              <DropdownItem
                onClick={() => handleNavigate('/client/accounts')}
                endContent={React.useMemo(
                  () => (
                    <SidebarChip ariaLabel={`${accountCount} аккаунтов`}>
                      {accountCount}
                    </SidebarChip>
                  ),
                  [accountCount]
                )}
                key="dashboard"
              >
                {t('sidebar.allAccounts')}
              </DropdownItem>

              <DropdownItem
                onClick={() => handleNavigate('/client/account/wallet')}
                endContent={<WalletChip />}
                key="settings"
              >
                {t('sidebar.balance')}
              </DropdownItem>
            </DropdownSection>
            <DropdownSection aria-label="Help & Feedback">
              <DropdownItem
                onClick={() => handleNavigate('/client/account/settings')}
                key="help_and_feedback"
              >
                {t('sidebar.settings')}
              </DropdownItem>
              {/* //@ts-ignore */}
              <DropdownItem key="logout" onClick={handleLogout}>
                {t('sidebar.logout')}
              </DropdownItem>
            </DropdownSection>
          </DropdownMenu>
        </Dropdown>
        <button
          type="button"
          aria-label={t('aria.closeSidebar')}
          className="size-10 p-2 icon opacity-50 active:opacity-100 md:hidden"
          onClick={onClose}
        >
          <Icon icon="line-md:close" />
        </button>
      </div>
    </div>
  );
});
const AccountItem: FC<{ item: any; onClick?: () => void }> = ({ item, onClick }) => {
  return (
    <a
      href={`/client/accounts/${item?.of_user_id}`}
      style={{ cursor: 'pointer' }}
      className="flex items-center gap-2 py-1.5"
      onClick={onClick}
    >
      <img src={item?.avatar} className="rounded-full w-4 h-4" />
      <Typography variant="body3">@{item?.of_user_name}</Typography>
    </a>
  );
};
SidebarHeader.displayName = 'SidebarHeader';

// Основной компонент сайдбара
const Component = React.memo<{ onItemClick?: () => void; onClose?: () => void }>(
  ({ onItemClick, onClose }) => {
    const { t } = useTranslations();
    const { user } = useAuth();
    const isPartner = user?.role === 'partner';
    return (
      <div className="h-full min-h-[48rem]">
        <div className="relative flex h-full w-full md:w-[18.1875rem] flex-1 flex-col md:border-r-small md:border-divider pt-0 pb-8 px-4 md:py-6">
          <SidebarHeader onClose={onClose} />
          <div
            className="md:-mr-6 h-full md:max-h-full py-6 md:pr-6"
            aria-label={t('aria.navigationMenu')}
          >
            <SidebarActiveTracker onItemClick={onItemClick} />
            <Spacer y={8} />
          </div>
          <div className="mt-auto pt-6 md:hidden">
            <a
              href={isPartner ? 'https://t.me/onlyu_partners' : 'https://t.me/onlyu_support'}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                startContent={<img src={IcTg} alt={t('alt.telegram')} />}
                className="h-10 rounded-3 border-[0.125rem] border-auth-strength-none text-auth-ink text-sm font-semibold leading-5 flex items-center justify-center text-center gap-1 bg-white w-full"
                style={{ boxShadow: 'none' }}
              >
                {t('buttons.support')}
              </Button>
            </a>
          </div>
        </div>
      </div>
    );
  }
);

Component.displayName = 'Component';

export default Component;
